import React from 'react';
import { DeleteOutlined, EyeOutlined, MessageOutlined, SettingOutlined } from '@ant-design/icons';
import { FmButton } from './FmButton';
import { FmConfirm } from './FmConfirm';
import { FmMenu, type FmMenuItem } from './FmMenu';
import { FmStack, FmText } from './FmComposition';
import type {
  EntityActionIconKey,
  EntityCardRemoveActionDescriptor,
  EntityIconActionDescriptor,
} from './FmEntityCardGrid';

const ACTION_ICONS: Record<EntityActionIconKey, React.ReactNode> = {
  overview: <EyeOutlined />,
  manage: <SettingOutlined />,
  assistant: <MessageOutlined />,
};

interface EntityCardManagementProps {
  managementAction?: EntityIconActionDescriptor;
  removeAction: EntityCardRemoveActionDescriptor;
}

interface EntityManagementMenuProps extends React.HTMLAttributes<HTMLElement> {
  items: FmMenuItem[];
  label: string;
  pending: boolean;
  testId?: string;
  menuTestId?: string;
}

const EntityManagementMenu: React.FC<EntityManagementMenuProps> = ({
  items,
  label,
  pending,
  testId,
  menuTestId,
}) => (
  <FmMenu items={items} placement="bottomRight" menuTestId={menuTestId}>
    <FmButton
      variant="text"
      leadingIcon={<SettingOutlined />}
      aria-label={label}
      title={label}
      data-testid={testId}
      disabled={pending}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {label}
    </FmButton>
  </FmMenu>
);

export const EntityCardManagement: React.FC<EntityCardManagementProps> = ({
  managementAction,
  removeAction,
}) => {
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const submittedRef = React.useRef(false);
  const automaticCloseRef = React.useRef(false);
  const { confirmation, pending, error } = removeAction;
  const pendingRef = React.useRef(pending);
  const managementLabel = managementAction?.ariaLabel ?? removeAction.label;

  React.useEffect(() => {
    const wasPending = pendingRef.current;
    pendingRef.current = pending;
    if (error) {
      submittedRef.current = false;
      setConfirmOpen(true);
    } else if (wasPending && !pending) {
      submittedRef.current = false;
    }
  }, [error, pending]);

  const requestRemove = (): void => {
    if (pending) return;
    setConfirmOpen(true);
  };

  const confirmRemove = (): void => {
    if (pending || submittedRef.current) return;
    submittedRef.current = true;
    automaticCloseRef.current = true;
    queueMicrotask(() => {
      automaticCloseRef.current = false;
    });
    removeAction.onClick();
  };

  const cancelRemove = (): void => {
    if (pending) return;
    automaticCloseRef.current = false;
    submittedRef.current = false;
    setConfirmOpen(false);
  };

  const handleConfirmOpenChange = (open: boolean): void => {
    if (!open && (pending || automaticCloseRef.current)) return;
    setConfirmOpen(open);
    if (!open) submittedRef.current = false;
  };

  const menuItems: FmMenuItem[] = [
    ...(managementAction
      ? [
          {
            key: managementAction.id,
            label: managementAction.ariaLabel,
            icon: ACTION_ICONS[managementAction.iconKey],
            'data-testid': managementAction.testId
              ? `${managementAction.testId}-menu-item`
              : undefined,
            onClick: managementAction.onClick,
          },
        ]
      : []),
    {
      key: removeAction.id,
      label: removeAction.label,
      icon: <DeleteOutlined />,
      danger: true,
      disabled: pending,
      'data-testid': removeAction.testId,
      onClick: requestRemove,
    },
  ];

  return (
    <FmConfirm
      open={confirmOpen}
      title={confirmation.title}
      description={
        <FmStack gap="xs">
          <FmText>{confirmation.description}</FmText>
          {error ? (
            <FmText as="div" tone="negative" role="alert" aria-live="assertive">
              {error}
            </FmText>
          ) : null}
        </FmStack>
      }
      affectedEntities={confirmation.affectedEntities}
      confirmText={confirmation.confirmLabel}
      cancelText={confirmation.cancelLabel}
      onConfirm={confirmRemove}
      onCancel={cancelRemove}
      onOpenChange={handleConfirmOpenChange}
      disabled={pending}
      loading={pending}
      danger
      contentTestId={removeAction.confirmTestId}
    >
      <EntityManagementMenu
        items={menuItems}
        label={managementLabel}
        pending={pending}
        testId={
          managementAction?.testId ??
          (removeAction.testId ? `${removeAction.testId}-management` : undefined)
        }
        menuTestId={removeAction.testId ? `${removeAction.testId}-menu` : undefined}
      />
    </FmConfirm>
  );
};

/**
 * A one-token title such as a cadastral id (491-543-4-38) is one reading unit:
 * a break at its hyphens reads as two numbers, so it never wraps.
 */
export const EntityCardTitleText: React.FC<{ title: string }> = ({ title }) =>
  /\s/.test(title.trim()) ? (
    title
  ) : (
    <span className="fm-entity-card__title-token">{title}</span>
  );
